// src/pages/offers/components/NewOfferModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../utils/supabase";
import { createOffer, updateOffer } from "../../../utils/api-offers";
import ServiceEditor from "../../../components/offers/ServiceEditor";
import ConditionsSection from "../../../components/offers/ConditionsSection";
import NewClientModal from "../../client-analytics/components/NewClientModal";

/* ──────────────────────────────────────────────────────────────
   Helpers (Sucursal)
─────────────────────────────────────────────────────────────── */
function formatBranchLabel(branch_no, branch_name) {
  if (branch_no == null) return "";
  const bn = String(branch_no).trim();
  const bname = (branch_name || "").trim();
  return bname ? `Sucursal ${bn} - ${bname}` : `Sucursal ${bn}`;
}

function formatClientDisplay(c) {
  if (!c) return "";
  const cn = c.client_number ?? "—";
  const bNo = c.branch_no != null ? ` ${c.branch_no}` : "";
  const bLabel = formatBranchLabel(c.branch_no, c.branch_name);
  const cuitLabel = c.cuit ? ` · CUIT ${c.cuit}` : (c.tax_id ? ` · ${c.tax_id}` : "");
  const branchExtra = bLabel ? ` · ${bLabel}` : "";
  return `${cn}${bNo} — ${c.name || "—"}${branchExtra}${cuitLabel}`;
}

function formatMoney(value, currency = "ARS") {
  const amount = Number(value) || 0;
  const symbol = currency === "USD" ? "US$" : "$";
  return `${symbol} ${amount.toFixed(2)}`;
}

function calcLineDiscountAmount(row = {}) {
  const base = Number(row.qty || 0) * Number(row.price || 0);
  const value = Math.max(0, Number(row.discount) || 0);
  const type = row.discount_type || "percentage";
  if (type === "fixed") return Math.min(base, value);
  return Math.min(base, base * (value / 100));
}

function calcLineNet(row = {}) {
  const base = Number(row.qty || 0) * Number(row.price || 0);
  return Math.max(0, base - calcLineDiscountAmount(row));
}

/* ──────────────────────────────────────────────────────────────
   BÚSQUEDA DE CLIENTES: Nº cliente, CUIT o nombre (smart search)
─────────────────────────────────────────────────────────────── */
async function searchClientsSmart(q, limit = 20) {
  const baseSelect = "id,name,client_number,cuit,tax_id,branch_no,branch_name";

  const s = (q ?? "").trim();
  if (!s) return [];

  const digitsOnly = s.replace(/\D/g, "");
  const isAllDigits = /^\d+$/.test(s);
  const isLikelyCuit = /^\d{8,11}$/.test(digitsOnly);

  if (isAllDigits && !isLikelyCuit) {
    const { data, error } = await supabase
      .from("clients")
      .select(baseSelect)
      .eq("client_number", Number(s))
      .order("branch_no", { ascending: true, nullsFirst: true })
      .order("name", { ascending: true })
      .limit(limit);
    if (error) throw error;
    return data ?? [];
  }

  if (isLikelyCuit) {
    const exact = await supabase
      .from("clients")
      .select(baseSelect)
      .eq("cuit", digitsOnly)
      .order("branch_no", { ascending: true, nullsFirst: true })
      .limit(limit);

    if (exact.error) throw exact.error;
    if ((exact.data ?? []).length) return exact.data;

    const like = await supabase
      .from("clients")
      .select(baseSelect)
      .ilike("cuit", `%${digitsOnly}%`)
      .order("branch_no", { ascending: true, nullsFirst: true })
      .order("name", { ascending: true })
      .limit(limit);

    if (like.error) throw like.error;
    return like.data ?? [];
  }

  const { data, error } = await supabase
    .from("clients")
    .select(baseSelect)
    .ilike("name", `%${s}%`)
    .order("name", { ascending: true })
    .order("branch_no", { ascending: true, nullsFirst: true })
    .limit(limit);

  if (error) throw error;
  return data ?? [];
}

/* ──────────────────────────────────────────────────────────────
   Obtener el próximo número de oferta
─────────────────────────────────────────────────────────────── */
async function getNextOfferNumber() {
  try {
    const { data, error } = await supabase
      .from("offers")
      .select("base_number")
      .order("base_number", { ascending: false })
      .limit(1);

    if (error) throw error;

    const lastNumber = data?.[0]?.base_number || 0;
    return lastNumber + 1;
  } catch (e) {
    console.error("Error al obtener siguiente número:", e);
    return null;
  }
}

export default function NewOfferModal({ isOpen, onClose, offer = null, load }) {
  if (!isOpen) return null;

  const isEdit = !!offer?.id;
  const [rev, setRev] = useState(offer?.revision ?? 0);
  const [nextOfferNumber, setNextOfferNumber] = useState(null);

  const [selectedClient, setSelectedClient] = useState(offer?.client || null);
  const [clientInfo, setClientInfo] = useState(null);
  const [notes, setNotes] = useState(offer?.notes || "");

  const [serviceType, setServiceType] = useState(
    offer?.service_type || "Laboratorio de Calibración"
  );
  const [certification, setCertification] = useState(
    offer?.certification || ""
  );
  const [contactName, setContactName] = useState(offer?.contact_name || "");
  const [contactEmail, setContactEmail] = useState(offer?.contact_email || "");

  const [discount, setDiscount] = useState(offer?.discount ?? 0);
  const [discountInput, setDiscountInput] = useState(String(offer?.discount ?? 0));
  const [discountType, setDiscountType] = useState(
    offer?.discount_type || "percentage"
  );

  const [services, setServices] = useState(
    (offer?.services || [{ order: 1, description: "", qty: 1, price: "" }]).map(
      (s, i) => ({
        id: s.id,
        order:
          s.position != null ? s.position + 1 : s.order || i + 1,
        description: s.description || "",
        qty: s.qty ?? 1,
        price: s.price ?? "",
        discount: s.discount ?? 0,
        discount_type: s.discount_type || "percentage",
      })
    )
  );

  const [validity_date, setValidityDate] = useState(
    offer?.validity_date || "15 días"
  );
  const [delivery_place, setDeliveryPlace] = useState(
    offer?.delivery_place || "Laboratorio"
  );
  const [delivery_time, setDeliveryTime] = useState(
    offer?.delivery_time || "Según coordinación"
  );
  const [payment_method, setPaymentMethod] = useState(
    offer?.payment_method || ""
  );
  const [price_currency, setPriceCurrency] = useState(
    offer?.price_currency || "USD BNA"
  );
  const [iva_rate, setIvaRate] = useState(
    offer?.iva_rate != null ? offer.iva_rate : 21
  );
  const [commercial_condition, setCommercialCondition] = useState(
    offer?.commercial_condition || ""
  );
  const [special_conditions, setSpecialConditions] = useState(
    offer?.special_conditions || ""
  );

  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [linkedPurchaseOrder, setLinkedPurchaseOrder] = useState(null);
  const [linkedPurchaseOrderLoading, setLinkedPurchaseOrderLoading] = useState(false);
  const [linkedPurchaseOrderInstrumentCount, setLinkedPurchaseOrderInstrumentCount] = useState(0);
  const clientChangeLocked =
    isEdit && offer?.status === "accepted" && linkedPurchaseOrderInstrumentCount > 0;

  useEffect(() => {
    if (isEdit) setRev(offer?.revision ?? 0);
  }, [isEdit, offer?.revision]);

  useEffect(() => {
    if (!isOpen || isEdit) return;

    (async () => {
      const nextNum = await getNextOfferNumber();
      setNextOfferNumber(nextNum);
    })();
  }, [isOpen, isEdit]);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedClient(offer?.client || null);
    setClientInfo(null);
    setNotes(offer?.notes || "");

    setServiceType(offer?.service_type || "Laboratorio de Calibración");
    setCertification(offer?.certification || "");
    setContactName(offer?.contact_name || "");
    setContactEmail(offer?.contact_email || "");

    setDiscount(offer?.discount ?? 0);
    setDiscountInput(String(offer?.discount ?? 0));
    setDiscountType(offer?.discount_type || "percentage");

    setServices(
      (offer?.services || [{ order: 1, description: "", qty: 1, price: "" }]).map(
        (s, i) => ({
          id: s.id,
          order: s.position != null ? s.position + 1 : (s.order || i + 1),
          description: s.description || "",
          qty: s.qty ?? 1,
          price: s.price ?? "",
          discount: s.discount ?? 0,
          discount_type: s.discount_type || "percentage",
        })
      )
    );

    setValidityDate(offer?.validity_date || "15 días");
    setDeliveryPlace(offer?.delivery_place || "Laboratorio");
    setDeliveryTime(offer?.delivery_time || "Según coordinación");
    setPaymentMethod(offer?.payment_method || "");
    setPriceCurrency(offer?.price_currency || "USD BNA");
    setIvaRate(offer?.iva_rate != null ? offer.iva_rate : 21);
    setCommercialCondition(offer?.commercial_condition || "");
    setSpecialConditions(offer?.special_conditions || "");

    setClientQuery("");
    setClientResults([]);
    setClientLoading(false);
    setNewClientOpen(false);
  }, [isOpen, offer]);

  useEffect(() => {
    const q = clientQuery.trim();
    if (!q || selectedClient) {
      setClientResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setClientLoading(true);
        const results = await searchClientsSmart(q, 20);
        setClientResults(results || []);
      } catch (e) {
        console.error("client search", e);
        setClientResults([]);
      } finally {
        setClientLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [clientQuery, selectedClient]);

  useEffect(() => {
    if (!selectedClient?.id) {
      setClientInfo(null);
      return;
    }
    (async () => {
      try {
        const { data, error } = await supabase
          .from("clients")
          .select(
            "id,name,cuit,tax_id,client_number,branch_no,branch_name,contact,email,phone,address"
          )
          .eq("id", selectedClient.id)
          .single();
        if (error) throw error;
        setClientInfo(data || null);
      } catch (e) {
        console.error("load client details", e);
        setClientInfo(null);
      }
    })();
  }, [selectedClient?.id]);

  useEffect(() => {
    if (!isOpen || !isEdit || !offer?.id) {
      setLinkedPurchaseOrder(null);
      setLinkedPurchaseOrderInstrumentCount(0);
      setLinkedPurchaseOrderLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLinkedPurchaseOrderLoading(true);
        const baseSelect = "id,code,offer_id,base_number,client_id,created_at";
        let po = null;

        const byOffer = await supabase
          .from("purchase_orders")
          .select(baseSelect)
          .eq("offer_id", offer.id)
          .maybeSingle();
        if (byOffer.error) throw byOffer.error;
        po = byOffer.data || null;

        if (!po && offer?.base_number != null) {
          const byBase = await supabase
            .from("purchase_orders")
            .select(baseSelect)
            .eq("base_number", offer.base_number)
            .maybeSingle();
          if (byBase.error) throw byBase.error;
          po = byBase.data || null;
        }

        let count = 0;
        if (po?.id) {
          const { count: cnt, error: cntErr } = await supabase
            .from("service_orders")
            .select("id", { count: "exact", head: true })
            .eq("po_id", po.id);
          if (cntErr) throw cntErr;
          count = Number(cnt) || 0;
        }

        if (!cancelled) {
          setLinkedPurchaseOrder(po);
          setLinkedPurchaseOrderInstrumentCount(count);
        }
      } catch (e) {
        console.warn("load linked purchase order", e);
        if (!cancelled) {
          setLinkedPurchaseOrder(null);
          setLinkedPurchaseOrderInstrumentCount(0);
        }
      } finally {
        if (!cancelled) setLinkedPurchaseOrderLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isEdit, offer?.id, offer?.base_number]);

  const ivaRateDecimal = useMemo(
    () =>
      Number(iva_rate) > 1
        ? Number(iva_rate) / 100
        : Number(iva_rate) || 0.21,
    [iva_rate]
  );
  const currency = useMemo(
    () => (String(price_currency).toUpperCase().includes("USD") ? "USD" : "ARS"),
    [price_currency]
  );

  const grossSubtotal = useMemo(() => {
    return services.reduce((acc, s) => {
      const qty = Number(s.qty) || 0;
      const price = Number(s.price) || 0;
      return acc + qty * price;
    }, 0);
  }, [services]);

  const lineDiscountAmount = useMemo(() => {
    return services.reduce((acc, s) => acc + calcLineDiscountAmount(s), 0);
  }, [services]);

  const subtotalBeforeDiscount = useMemo(() => {
    return services.reduce((acc, s) => acc + calcLineNet(s), 0);
  }, [services]);

  const discountAmount = useMemo(() => {
    const discountValue = Number(discount) || 0;
    if (discountType === "percentage") {
      return Math.min(subtotalBeforeDiscount, subtotalBeforeDiscount * (discountValue / 100));
    }
    return Math.min(discountValue, subtotalBeforeDiscount);
  }, [discount, discountType, subtotalBeforeDiscount]);

  const subtotalAfterDiscount = Math.max(0, subtotalBeforeDiscount - discountAmount);
  const ivaAmount = subtotalAfterDiscount * ivaRateDecimal;
  const totalWithIva = subtotalAfterDiscount + ivaAmount;

  function applyDiscountFromInput() {
    const parsed = Number(discountInput);
    if (Number.isNaN(parsed)) {
      alert("Ingresá un valor de descuento válido.");
      return false;
    }
    if (discountType === "percentage" && (parsed < 0 || parsed > 100)) {
      alert("El descuento porcentual debe estar entre 0 y 100.");
      return false;
    }
    if (discountType === "fixed" && parsed < 0) {
      alert("El descuento fijo no puede ser negativo.");
      return false;
    }

    setDiscount(parsed);
    return true;
  }

  const offerNumberDisplay = isEdit
    ? `CT-${offer?.base_number ?? ""}-${String(rev).padStart(2, "0")}`
    : nextOfferNumber
    ? `CT-${nextOfferNumber}-00`
    : "CT-####-00";

  async function handleSubmit() {
    if (!selectedClient?.id) {
      alert("Elegí un cliente.");
      return;
    }

    const parsedDiscount = Number(discountInput);
    if (Number.isNaN(parsedDiscount)) {
      alert("Ingresá un valor de descuento válido.");
      return;
    }
    if (discountType === "percentage" && (parsedDiscount < 0 || parsedDiscount > 100)) {
      alert("El descuento porcentual debe estar entre 0 y 100.");
      return;
    }
    if (discountType === "fixed" && parsedDiscount < 0) {
      alert("El descuento fijo no puede ser negativo.");
      return;
    }

    const payload = {
      client_id: selectedClient.id,
      notes,
      service_type: serviceType,
      certification: certification.trim() || undefined,
      contact_name: contactName.trim() || undefined,
      contact_email: contactEmail.trim() || undefined,
      discount: parsedDiscount || 0,
      discount_type: discountType,
      services: services
        .filter((r) => (r.description || "").trim())
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((r, i) => ({
          description: r.description.trim(),
          qty: Number(r.qty || 1),
          price: Number(r.price || 0),
          discount: Number(r.discount || 0),
          discount_type: r.discount_type || "percentage",
          position: i,
        })),
      validity_date,
      delivery_place,
      delivery_time,
      payment_method,
      price_currency,
      iva_rate,
      commercial_condition: commercial_condition?.trim() || undefined,
      special_conditions,
      vat_rate: ivaRateDecimal,
    };

    if (isEdit && rev !== offer.revision) {
      if (rev < 0 || rev > 99) {
        alert("La revisión debe estar entre 0 y 99.");
        return;
      }
      payload.revision = rev;
    }

    try {
      if (isEdit) {
        const oldClientId = offer?.client_id ?? offer?.client?.id ?? null;
        const newClientId = selectedClient.id;

        if (oldClientId && newClientId !== oldClientId) {
          const hasLinkedPo = !!linkedPurchaseOrder?.id;
          const hasLinkedPoItems = linkedPurchaseOrderInstrumentCount > 0;

          if (hasLinkedPo && hasLinkedPoItems) {
            alert(
              "No se puede cambiar el cliente: la orden de servicio ya tiene instrumentos cargados. Primero hay que vaciar o corregir esa orden."
            );
            return;
          }

          const { error: rpcErr } = await supabase.rpc("update_offer_client", {
            p_offer_id: offer.id,
            p_new_client_id: newClientId,
          });

          if (rpcErr) {
            console.error("RPC update_offer_client error:", rpcErr);
            alert(
              rpcErr?.message ||
                "No se pudo cambiar el cliente de la oferta. Revisá el estado de la orden de servicio."
            );
            return;
          }
        }
        await updateOffer(offer.id, payload);
      } else {
        await createOffer(payload);
      }

      await load?.();
      onClose?.();
    } catch (e) {
      const msg = e?.message?.toLowerCase?.() || "";
      if (
        msg.includes("unique") &&
        msg.includes("base_number") &&
        msg.includes("revision")
      ) {
        alert(
          "Esa revisión ya existe para esta oferta. Elegí otro número de revisión."
        );
        return;
      }
      console.error(e);
      alert(e?.message || "Error al guardar la oferta");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto">
      <div className="m-4 w-full max-w-7xl rounded-2xl bg-background text-foreground shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold">
              {isEdit ? "Editar oferta" : "Nueva oferta"}
            </h3>
            <p className="text-sm text-muted-foreground">
              {isEdit
                ? "Modificá los datos de la oferta"
                : "Completá los datos para generar la oferta"}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Oferta</div>
              <div className="text-lg font-bold text-primary">
                {offerNumberDisplay}
              </div>
            </div>
            <button
              className="px-3 py-1 rounded-lg border hover:bg-muted/40"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {isEdit && (
            <div className="flex items-center justify-end gap-2 pb-3 border-b border-border">
              <label className="text-sm font-medium text-foreground/70">
                Revisión
              </label>
              <input
                type="number"
                min={0}
                max={99}
                step={1}
                inputMode="numeric"
                value={rev}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (!Number.isNaN(v) && v >= 0 && v <= 99) setRev(v);
                }}
                className="w-20 field text-center"
              />
            </div>
          )}

          <div>
            <label className="text-sm font-medium">Cliente</label>
            <div className="relative">
              <input
                disabled={clientChangeLocked}
                className={`w-full rounded-lg border border-border bg-background px-3 py-2 ${
                  clientChangeLocked ? "cursor-not-allowed opacity-60" : ""
                }`}
                placeholder="Buscar por Nº cliente, nombre o CUIT…"
                value={selectedClient ? formatClientDisplay(selectedClient) : clientQuery}
                onChange={(e) => {
                  if (clientChangeLocked) return;
                  setSelectedClient(null);
                  setClientInfo(null);
                  setClientQuery(e.target.value);
                }}
              />

              {!selectedClient &&
                clientQuery.trim() &&
                (clientLoading || clientResults.length > 0) && (
                  <div className="absolute left-0 right-0 z-[90] mt-1 w-full max-h-72 overflow-auto rounded-xl border border-border shadow-2xl bg-white dark:bg-zinc-900">
                    {clientLoading && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        Buscando…
                      </div>
                    )}

                    {!clientLoading &&
                      clientResults.map((c) => {
                        const branchLabel = formatBranchLabel(c.branch_no, c.branch_name);
                        const cn = c.client_number ?? "—";
                        const bNo = c.branch_no != null ? ` ${c.branch_no}` : "";
                        return (
                          <button
                            key={c.id}
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-muted/40 focus:bg-muted/50 focus:outline-none"
                            onClick={() => {
                              setSelectedClient(c);
                              setClientQuery("");
                              setClientResults([]);
                            }}
                          >
                            <div className="text-sm font-medium text-foreground">
                              {cn}
                              {bNo} — {c.name}
                              {branchLabel ? (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  ({branchLabel})
                                </span>
                              ) : null}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {c.cuit ? `CUIT ${c.cuit}` : c.tax_id || "—"}
                            </div>
                          </button>
                        );
                      })}

                    {!clientLoading && clientResults.length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        Sin resultados
                      </div>
                    )}
                  </div>
                )}
            </div>

            <div className="mt-2 flex flex-col md:flex-row md:items-start md:gap-3">
              <button
                type="button"
                disabled={clientChangeLocked}
                className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-sm hover:bg-muted/40 ${
                  clientChangeLocked ? "cursor-not-allowed opacity-60 hover:bg-transparent" : ""
                }`}
                onClick={() => setNewClientOpen(true)}
              >
                + Nuevo cliente
              </button>

              {clientInfo && (
                <div className="mt-3 md:mt-0 grow rounded-lg border border-border bg-muted/10 px-4 py-3">
                  <div className="flex items-start justify-between">
                    <div className="text-base font-semibold">
                      {clientInfo.name}
                    </div>
                    <button
                      type="button"
                      disabled={clientChangeLocked}
                      className={`text-xs underline text-muted-foreground hover:text-foreground ml-2 ${
                        clientChangeLocked ? "cursor-not-allowed opacity-60 hover:text-muted-foreground" : ""
                      }`}
                      onClick={() => {
                        if (clientChangeLocked) return;
                        setSelectedClient(null);
                        setClientInfo(null);
                      }}
                    >
                      Cambiar
                    </button>
                  </div>

                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                    <div>
                      <span className="text-muted-foreground">CUIT:</span>
                      <span className="ml-2 font-medium">
                        {clientInfo.cuit || clientInfo.tax_id || "—"}
                      </span>
                    </div>

                    <div>
                      <span className="text-muted-foreground">Nº Cliente:</span>
                      <span className="ml-2 font-medium">
                        {clientInfo.client_number || "—"}
                      </span>
                    </div>

                    <div>
                      <span className="text-muted-foreground">Sucursal:</span>
                      <span className="ml-2 font-medium">
                        {formatBranchLabel(clientInfo.branch_no, clientInfo.branch_name) || "—"}
                      </span>
                    </div>

                    <div>
                      <span className="text-muted-foreground">Contacto:</span>
                      <span className="ml-2">
                        {clientInfo.contact || "—"}
                      </span>
                    </div>

                    <div>
                      <span className="text-muted-foreground">Teléfono:</span>
                      <span className="ml-2">{clientInfo.phone || "—"}</span>
                    </div>

                    <div className="sm:col-span-2">
                      <span className="text-muted-foreground">Email:</span>
                      <span className="ml-2 truncate">
                        {clientInfo.email || "—"}
                      </span>
                    </div>

                    <div className="sm:col-span-2">
                      <span className="text-muted-foreground">Dirección:</span>
                      <span className="ml-2">
                        {clientInfo.address || "—"}
                      </span>
                    </div>
                  </div>
                </div>
              )}
          </div>

            {isEdit && offer?.status === "accepted" && (
              <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm">
                <div className="font-semibold text-amber-700 dark:text-amber-400">
                  Ayuda para gerencia
                </div>
                <div className="mt-1 text-muted-foreground">
                  Si esta oferta ya generó una orden de servicio, el cambio de cliente
                  solo debe hacerse cuando la orden esté vacía.
                </div>
              </div>
            )}

            {isEdit && offer?.status === "accepted" && linkedPurchaseOrderLoading && (
              <div className="mt-3 rounded-xl border border-border bg-muted/10 px-4 py-3 text-sm text-muted-foreground">
                Verificando la orden de servicio vinculada...
              </div>
            )}

            {isEdit && offer?.status === "accepted" && linkedPurchaseOrder?.id && (
              <div
                className={`mt-3 rounded-xl px-4 py-3 text-sm ${
                  linkedPurchaseOrderInstrumentCount > 0
                    ? "border border-red-500/30 bg-red-500/10"
                    : "border border-amber-500/30 bg-amber-500/10"
                }`}
              >
                <div
                  className={`font-semibold ${
                    linkedPurchaseOrderInstrumentCount > 0
                      ? "text-red-700 dark:text-red-400"
                      : "text-amber-700 dark:text-amber-400"
                  }`}
                >
                  {linkedPurchaseOrderInstrumentCount > 0
                    ? "Bloqueo de trazabilidad"
                    : "Cambio permitido con validación"}
                </div>
                <div className="mt-1 text-muted-foreground">
                  {linkedPurchaseOrder.code
                    ? `OS vinculada: ${linkedPurchaseOrder.code}. `
                    : "OS vinculada detectada. "}
                  {linkedPurchaseOrderInstrumentCount > 0
                    ? `Tiene ${linkedPurchaseOrderInstrumentCount} instrumento(s) cargado(s), por lo que no se puede cambiar la razón social desde esta oferta.`
                    : "No tiene instrumentos cargados todavía, así que el cliente puede corregirse y la OS se sincronizará."}
                </div>
                {linkedPurchaseOrderInstrumentCount > 0 && (
                  <div className="mt-2 text-xs text-red-700/80 dark:text-red-300/80">
                    Ayuda para administración: si el cliente es incorrecto y ya hay instrumentos,
                    la corrección debe hacerse sobre la trazabilidad de esos instrumentos, no solo
                    sobre la oferta.
                  </div>
                )}
                {linkedPurchaseOrderInstrumentCount > 0 && (
                  <div className="mt-2 text-xs font-medium text-red-800 dark:text-red-200">
                    El campo de cliente quedó bloqueado para evitar inconsistencias.
                  </div>
                )}
              </div>
            )}

            <NewClientModal
              isOpen={newClientOpen}
              onClose={() => setNewClientOpen(false)}
              onCreated={(row) => {
                setSelectedClient(row);
                setClientQuery("");
                setClientResults([]);
                setNewClientOpen(false);
              }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Tipo de servicio</label>
              <select
                className="w-full rounded-lg border border-border bg-background px-3 py-2"
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
              >
                <option value="Laboratorio de Calibración">
                  Laboratorio de Calibración
                </option>
                <option value="Laboratorio Electrónica">
                  Laboratorio Electrónica
                </option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium">Certificación</label>
              <input
                type="text"
                className="w-full rounded-lg border border-border bg-background px-3 py-2"
                placeholder="Código de certificación"
                value={certification}
                onChange={(e) => setCertification(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Contacto - Nombre</label>
              <input
                type="text"
                className="w-full rounded-lg border border-border bg-background px-3 py-2"
                placeholder="Nombre del contacto"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Contacto - Email</label>
              <input
                type="email"
                className="w-full rounded-lg border border-border bg-background px-3 py-2"
                placeholder="email@ejemplo.com"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Observaciones</label>
            <textarea
              className="w-full rounded-lg border border-border bg-background px-3 py-2"
              placeholder="Notas internas / alcance…"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* Sección de Servicios con Botón al final */}
          <div>
            <ServiceEditor
              currency={currency}
              ivaRate={ivaRateDecimal}
              rows={services}
              onChange={setServices}
            />
            {/* Botón "+ Agregar servicio" reubicado aquí abajo a la derecha */}
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                className="inline-flex items-center rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600"
                onClick={() =>
                  setServices((prev) => [
                    ...prev,
                    {
                      order: prev.length + 1,
                      description: "",
                      qty: 1,
                      price: "",
                      discount: 0,
                      discount_type: "percentage",
                    },
                  ])
                }
              >
                + Agregar servicio
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
            <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
              <div>
                <h4 className="text-base font-semibold">Descuento</h4>
                <p className="text-sm text-muted-foreground">
                  Aplicalo sobre el subtotal después de los descuentos por ítem.
                </p>
              </div>
              <button
                type="button"
                className="inline-flex items-center rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
                onClick={applyDiscountFromInput}
              >
                Aplicar descuento
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="text-sm font-medium">Tipo</label>
                <select
                  className="w-full rounded-lg border border-border bg-background px-3 py-2"
                  value={discountType}
                  onChange={(e) => setDiscountType(e.target.value)}
                >
                  <option value="percentage">Porcentual (%)</option>
                  <option value="fixed">Monto fijo ({currency})</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">
                  {discountType === "percentage" ? "Porcentaje (%)" : `Monto (${currency})`}
                </label>
                <input
                  type="number"
                  min="0"
                  step={discountType === "percentage" ? "0.1" : "0.01"}
                  max={discountType === "percentage" ? "100" : undefined}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2"
                  placeholder={discountType === "percentage" ? "5" : "0.00"}
                  value={discountInput}
                  onChange={(e) => setDiscountInput(e.target.value)}
                />
              </div>

              <div className="flex flex-col justify-end">
                <div className="rounded-lg border border-amber-300 bg-amber-100 px-3 py-2 dark:border-amber-700 dark:bg-amber-900/20">
                  <div className="text-xs font-medium text-amber-700 dark:text-amber-400">
                    Descuento aplicado
                  </div>
                  <div className="text-lg font-bold text-amber-800 dark:text-amber-300">
                    {formatMoney(discountAmount, currency)}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 border-t border-amber-500/20 pt-4">
              <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                <div className="text-muted-foreground">Subtotal bruto</div>
                <div className="text-right font-medium">{formatMoney(grossSubtotal, currency)}</div>

                {lineDiscountAmount > 0 && (
                  <>
                    <div className="font-medium text-orange-700 dark:text-orange-400">
                      Descuentos por ítem
                    </div>
                    <div className="text-right font-medium text-orange-700 dark:text-orange-400">
                      - {formatMoney(lineDiscountAmount, currency)}
                    </div>
                  </>
                )}

                <div className="text-muted-foreground">Subtotal antes del descuento general</div>
                <div className="text-right font-medium">{formatMoney(subtotalBeforeDiscount, currency)}</div>

                <div className="font-medium text-amber-700 dark:text-amber-400">
                  Descuento general ({discountType === "percentage" ? `${discount}%` : "monto fijo"})
                </div>
                <div className="text-right font-medium text-amber-700 dark:text-amber-400">
                  - {formatMoney(discountAmount, currency)}
                </div>

                <div className="text-muted-foreground">Subtotal con descuento</div>
                <div className="text-right font-medium">{formatMoney(subtotalAfterDiscount, currency)}</div>

                <div className="text-muted-foreground">IVA ({Number(iva_rate)}%)</div>
                <div className="text-right font-medium">{formatMoney(ivaAmount, currency)}</div>

                <div className="pt-2 text-base font-bold text-foreground md:border-t md:border-border">
                  Total final
                </div>
                <div className="pt-2 text-right text-base font-bold text-primary md:border-t md:border-border">
                  {formatMoney(totalWithIva, currency)}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-5">
            <h4 className="text-base font-semibold mb-4">
              Condiciones Comerciales
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Validez de la oferta</label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2"
                  placeholder="Ej: 15 días"
                  value={validity_date}
                  onChange={(e) => setValidityDate(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Lugar de entrega</label>
                <select
                  className="w-full rounded-lg border border-border bg-background px-3 py-2"
                  value={delivery_place}
                  onChange={(e) => setDeliveryPlace(e.target.value)}
                >
                  <option value="Laboratorio">Laboratorio</option>
                  <option value="In Situ Cliente">In Situ Cliente</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">Plazo de entrega</label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2"
                  placeholder="Ej: Según coordinación"
                  value={delivery_time}
                  onChange={(e) => setDeliveryTime(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Forma de pago</label>
                <select
                  className="w-full rounded-lg border border-border bg-background px-3 py-2"
                  value={payment_method}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <option value="">Seleccionar...</option>
                  <option value="Contado">Contado</option>
                  <option value="15 DFF">15 DFF</option>
                  <option value="30 DFF">30 DFF</option>
                  <option value="60 DFF">60 DFF</option>
                  <option value="90 DFF">90 DFF</option>
                  <option value="120 DFF">120 DFF</option>
                  <option value="Anticipo 50%">Anticipo 50%</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">Moneda</label>
                <select
                  className="w-full rounded-lg border border-border bg-background px-3 py-2"
                  value={price_currency}
                  onChange={(e) => setPriceCurrency(e.target.value)}
                >
                  <option value="USD BNA">USD BNA</option>
                  <option value="ARS">ARS</option>
                  <option value="USD">USD</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">IVA (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2"
                  value={iva_rate}
                  onChange={(e) => setIvaRate(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="text-sm font-medium">
                Condición comercial (resumen)
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-border bg-background px-3 py-2"
                placeholder="Ej: Anticipo / Ent. Lab / USD BNA"
                value={commercial_condition}
                onChange={(e) => setCommercialCondition(e.target.value)}
              />
            </div>

            <div className="mt-4">
              <label className="text-sm font-medium">
                Condiciones especiales
              </label>
              <textarea
                className="w-full rounded-lg border border-border bg-background px-3 py-2"
                placeholder="Condiciones adicionales..."
                rows={3}
                value={special_conditions}
                onChange={(e) => setSpecialConditions(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-border px-5 py-4">
          <button
            className="px-4 py-2 rounded-xl border hover:bg-muted/40"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            className="px-4 py-2 rounded-xl bg-primary text-primary-foreground hover:opacity-90"
            onClick={handleSubmit}
          >
            {isEdit ? "Guardar cambios" : "Crear oferta"}
          </button>
        </div>
      </div>
    </div>
  );
}
